/**
 * Deterministic pseudo-random numbers.
 *
 * Rules must never call `Math.random()`: the server and the predicting client
 * have to walk the same match from the same seed, or the board snaps back on
 * every reconciliation. `RuleContext.random` is always one of these.
 *
 * Algorithm: xmur3 seeding into mulberry32. Small, fast, no dependencies, and
 * identical across engines because it stays inside 32-bit integer arithmetic.
 */

export interface Rng {
  /** Uniform in [0, 1). */
  (): number;
}

export interface SeededRng extends Rng {
  /** Current internal state, so a match can be resumed mid-stream. */
  readonly state: () => number;
  /** Restore a previously captured state. */
  readonly restore: (state: number) => void;
}

function xmur3(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

export function createRng(seed: string): SeededRng {
  let a = xmur3(seed)();

  const rng = (() => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }) as SeededRng;

  return Object.assign(rng, {
    state: () => a >>> 0,
    restore: (next: number) => {
      a = next | 0;
    },
  });
}

/** Integer in [min, max], inclusive. */
export function randomInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/** Uniform pick. Returns `undefined` only for an empty list. */
export function randomPick<T>(rng: Rng, items: readonly T[]): T | undefined {
  if (items.length === 0) return undefined;
  return items[Math.floor(rng() * items.length)];
}

/** Fisher-Yates, on a copy. Deterministic for a given rng stream. */
export function shuffle<T>(rng: Rng, items: readonly T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i] as T;
    const b = out[j] as T;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

/**
 * A match seed. Generated on the server only — a client-chosen seed is a client
 * that gets to pick its own board.
 */
export function createSeed(random: () => number = Math.random): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 16; i++) {
    out += alphabet[Math.floor(random() * alphabet.length)];
  }
  return out;
}
